import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";

const testimonials = [
  {
    name: "Sarah Mitchell",
    role: "CTO at TechVision Global",
    company: "TechVision Global",
    image: "/images/Web Development.jpg",
    content:
      "Kolaborate transformed our development capacity overnight. Their AI engineers integrated seamlessly with our team and delivered exceptional results within the first month. The quality of talent and professionalism exceeded our expectations.",
    rating: 5,
    project: "AI-Powered Analytics Platform",
  },
  {
    name: "Michael Chen",
    role: "Head of Digital Innovation",
    company: "Global Financial Services",
    image: "/images/data-processing.jpeg",
    content:
      "Working with Kolaborate's data annotation team was game-changing for our machine learning initiatives. They delivered precise, high-quality labeled datasets that accelerated our model training by 60%. Outstanding service and attention to detail.",
    rating: 5,
    project: "ML Model Training Dataset",
  },
  {
    name: "Elena Rodriguez",
    role: "Marketing Director",
    company: "Creative Solutions Inc",
    image: "/images/Graphics Design.jpg",
    content:
      "The creative team from Kolaborate brought our brand vision to life with stunning video content and marketing campaigns. Their understanding of global markets and creative excellence made them an invaluable extension of our team.",
    rating: 5,
    project: "Brand Campaign & Video Production",
  },
  {
    name: "David Thompson",
    role: "CEO & Founder",
    company: "StartupLab Ventures",
    image: "/images/bpo.jpg",
    content:
      "As a growing startup, we needed reliable development expertise without the overhead. Kolaborate provided us with senior full-stack developers who understood our vision and delivered a production-ready platform in record time.",
    rating: 5,
    project: "Full-Stack Platform Development",
  },
];

export const TestimonialsSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length,
    );
  };

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-10 left-10 w-20 h-20 bg-brand-primary/30 rounded-full animate-float"></div>
      <div
        className="absolute bottom-10 right-10 w-16 h-16 bg-brand-tertiary/30 rounded-full animate-float"
        style={{ animationDelay: "1s" }}
      ></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16 animate-fadeInUp">
          <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/80 backdrop-blur-sm text-brand-tertiary text-sm font-semibold mb-6">
            <Star className="w-4 h-4 mr-2 fill-current" />
            Client Success Stories
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-brand-primary">
            What Our Clients Say
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Don't just take our word for it. Hear from the leaders who've
            transformed their teams with Kolaborate.
          </p>
        </div>

        <div className="relative">
          <div className="bg-white border border-gray-200 rounded-3xl shadow-xl p-8 md:p-12 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
              {/* Client Image & Info */}
              <div className="text-center lg:text-left">
                <div className="relative inline-block mb-6">
                  <img
                    src={testimonials[currentTestimonial].image}
                    alt={testimonials[currentTestimonial].name}
                    className="w-32 h-32 rounded-3xl object-cover shadow-lg mx-auto lg:mx-0"
                  />
                  <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-gradient-to-r from-brand-primary to-brand-tertiary rounded-2xl flex items-center justify-center shadow-lg">
                    <Quote className="text-white" size={20} />
                  </div>
                </div>
                <h4 className="text-xl font-bold text-brand-grey mb-2">
                  {testimonials[currentTestimonial].name}
                </h4>
                <p className="text-brand-primary font-semibold mb-1">
                  {testimonials[currentTestimonial].role}
                </p>
                <p className="text-gray-500 text-sm mb-4">
                  {testimonials[currentTestimonial].company}
                </p>
                <div className="flex justify-center lg:justify-start space-x-1 mb-4">
                  {[...Array(testimonials[currentTestimonial].rating)].map(
                    (_, i) => (
                      <Star
                        key={i}
                        className="w-5 h-5 text-yellow-400 fill-current"
                      />
                    ),
                  )}
                </div>
                <div className="inline-block px-4 py-2 bg-gradient-to-r from-brand-secondary to-brand-secondary text-brand-tertiary rounded-full text-sm font-medium">
                  {testimonials[currentTestimonial].project}
                </div>
              </div>

              {/* Testimonial Content */}
              <div className="lg:col-span-2">
                <div className="relative">
                  <Quote
                    className="absolute -top-4 -left-2 text-brand-primary/20"
                    size={40}
                  />
                  <blockquote className="text-gray-600 text-lg leading-relaxed pl-6 pr-4">
                    "{testimonials[currentTestimonial].content}"
                  </blockquote>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex justify-center items-center mt-8 space-x-4">
            <button
              onClick={prevTestimonial}
              className="w-12 h-12 bg-white border border-gray-200 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group hover:border-brand-primary"
            >
              <ChevronLeft
                className="text-brand-grey group-hover:text-brand-primary"
                size={20}
              />
            </button>

            <div className="flex space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentTestimonial
                      ? "bg-brand-primary w-8"
                      : "bg-gray-300 hover:bg-brand-primary/50"
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="w-12 h-12 bg-white border border-gray-200 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group hover:border-brand-primary"
            >
              <ChevronRight
                className="text-brand-grey group-hover:text-brand-primary"
                size={20}
              />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
